// ============================================
// pages/Salon/SalonBillHistory.js
// All-time Bill History with Pagination & Filters
// ============================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { SALON_API_URL } from '../../api/config';
import {
  ReceiptText, Search,  X, ChevronDown, ChevronUp,
  Banknote, Smartphone, CreditCard, SplitSquareVertical,
  Scissors, Package, User, Phone, Clock, Tag,
  Loader2, Calendar,
  TrendingUp, Hash, SlidersHorizontal,
} from 'lucide-react';

// ─── Helpers ───────────────────────────────────────────────────────────────
function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}
function fmtTime(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return '—'; }
}

const PAYMENT_META = {
  cash:  { Icon: Banknote,            label: 'Cash',  light: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  upi:   { Icon: Smartphone,          label: 'UPI',   light: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' },
  card:  { Icon: CreditCard,          label: 'Card',  light: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800' },
  split: { Icon: SplitSquareVertical, label: 'Split', light: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800' },
};

const PAGE_SIZE = 15;

// ─── Bill Row ──────────────────────────────────────────────────────────────
function BillRow({ bill }) {
  const [expanded, setExpanded] = useState(false);
  const meta     = PAYMENT_META[bill.payment_method] || PAYMENT_META.cash;
  const { Icon } = meta;
  const items    = bill.items || [];
  const services = items.filter(i => i.item_type === 'service');
  const products = items.filter(i => i.item_type === 'product');
  const discount = parseFloat(bill.discount || 0);

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl border-2 transition-all duration-300 overflow-hidden ${expanded ? 'shadow-md border-purple-500 dark:border-purple-600' : 'shadow-sm border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-600 hover:shadow-md'}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left"
      >
        {/* Bill # badge */}
        <div className="w-11 h-11 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-black text-purple-600 dark:text-purple-400">#{bill.bill_number}</span>
        </div>

        {/* Customer + date/time */}
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold text-gray-800 dark:text-white truncate">
            {bill.customer_name || 'Walk-in Customer'}
          </p>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {expanded && bill.customer_phone && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Phone size={9}/>{bill.customer_phone}
              </span>
            )}
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Calendar size={9}/>{fmtDate(bill.created_at)}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Clock size={9}/>{fmtTime(bill.created_at)}
            </span>
            {(services.length > 0 || products.length > 0) && (
              <span className="text-xs text-gray-400">
                {services.length > 0 && `${services.length} svc`}
                {services.length > 0 && products.length > 0 && ' · '}
                {products.length > 0 && `${products.length} prod`}
              </span>
            )}
          </div>
        </div>

        {/* Payment + total */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`hidden sm:flex items-center justify-center gap-1.5 text-xs font-bold w-20 py-1.5 rounded-xl border flex-shrink-0 ${meta.light}`}>
            <Icon size={11}/>{meta.label}
          </span>
          <div className="text-right w-24 flex-shrink-0">
            <p className="text-lg font-black text-gray-800 dark:text-white">{fmt(bill.total)}</p>
            {discount > 0 ? (
              <p className="text-xs text-green-500 font-semibold">−{fmt(discount)} off</p>
            ) : (
              <p className="text-xs select-none opacity-0">—</p>
            )}
          </div>
          <div className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${expanded ? 'bg-purple-100 dark:bg-purple-900/30' : 'bg-gray-100 dark:bg-slate-700'}`}>
            {expanded
              ? <ChevronUp size={14} className="text-purple-600"/>
              : <ChevronDown size={14} className="text-gray-400"/>
            }
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 px-5 py-4 space-y-4">
          {services.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Services</p>
              <div className="space-y-2">
                {services.map((svc, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                        <Scissors size={12} className="text-purple-500"/>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                          {svc.item_name}{svc.item_variant ? ` · ${svc.item_variant}` : ''}
                        </p>
                        {svc.staff_name && (
                          <p className="text-xs text-gray-400 flex items-center gap-1"><User size={9}/>{svc.staff_name}</p>
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-black text-gray-800 dark:text-white flex-shrink-0">{fmt(svc.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {products.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Products</p>
              <div className="space-y-2">
                {products.map((prod, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <Package size={12} className="text-blue-500"/>
                      </div>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 truncate">
                        {prod.item_name} <span className="text-gray-400 font-normal">× {prod.qty || 1}</span>
                      </p>
                    </div>
                    <span className="text-sm font-black text-gray-800 dark:text-white flex-shrink-0">{fmt(parseFloat(prod.price) * (prod.qty || 1))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 dark:border-slate-600 pt-3 space-y-1.5">
            <div className="flex justify-between text-sm text-gray-500">
              <span>Subtotal</span>
              <span>{fmt(parseFloat(bill.total) + discount)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-600 flex items-center gap-1"><Tag size={11}/>Discount</span>
                <span className="font-semibold text-green-600">−{fmt(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-black text-base pt-1">
              <span className="text-gray-800 dark:text-white">Total Paid</span>
              <span className="text-purple-600 dark:text-purple-400">{fmt(bill.total)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filter Drawer ─────────────────────────────────────────────────────────
function FilterDrawer({ open, onClose, filters, onChange, onReset }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative ml-auto w-full max-w-sm bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800">
          <h2 className="font-black text-gray-900 dark:text-white text-lg flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-purple-600"/> Filters
          </h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400">
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Search */}
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Customer Name / Phone</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
              <input
                className="w-full pl-8 pr-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Search customer…"
                value={filters.search}
                onChange={e => onChange({ ...filters, search: e.target.value })}
              />
            </div>
          </div>

          {/* Date range */}
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">From</p>
                <input type="date"
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={filters.startDate}
                  onChange={e => onChange({ ...filters, startDate: e.target.value })}
                />
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">To</p>
                <input type="date"
                  className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={filters.endDate}
                  onChange={e => onChange({ ...filters, endDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Payment method */}
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Payment Method</label>
            <div className="grid grid-cols-2 gap-2">
              {['all', 'cash', 'upi', 'card', 'split'].map(m => (
                <button key={m}
                  onClick={() => onChange({ ...filters, paymentMethod: m })}
                  className={`py-2.5 rounded-xl text-xs font-bold capitalize border-2 transition-all ${
                    filters.paymentMethod === m
                      ? 'bg-purple-600 border-purple-600 text-white'
                      : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                  }`}>
                  {m === 'all' ? 'All Methods' : m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Amount range */}
          <div>
            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Amount Range</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">₹</span>
                <input type="number" min="0" placeholder="Min"
                  className="w-full pl-7 pr-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={filters.minAmount}
                  onChange={e => onChange({ ...filters, minAmount: e.target.value })}
                />
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs font-bold">₹</span>
                <input type="number" min="0" placeholder="Max"
                  className="w-full pl-7 pr-3 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  value={filters.maxAmount}
                  onChange={e => onChange({ ...filters, maxAmount: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 dark:border-slate-800 flex gap-3">
          <button onClick={onReset}
            className="flex-1 py-3 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            Reset
          </button>
          <button onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold transition-colors">
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────
const DEFAULT_FILTERS = {
  search: '',
  startDate: '',
  endDate: '',
  paymentMethod: 'all',
  minAmount: '',
  maxAmount: '',
};

export default function SalonBillHistory() {
  const { userToken: token } = useAuth();

  const [bills,       setBills]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [filters,     setFilters]     = useState(DEFAULT_FILTERS);
  const [drawerOpen,  setDrawerOpen]  = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);

  const activeFilterCount = [
    filters.search,
    filters.startDate,
    filters.endDate,
    filters.paymentMethod !== 'all' ? filters.paymentMethod : '',
    filters.minAmount,
    filters.maxAmount,
  ].filter(Boolean).length;

  const fetchBills = useCallback(async (pg, f, { append = false } = {}) => {
    if (!token) return;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const offset = Math.max(0, (pg - 1) * PAGE_SIZE);
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
      if (f.search)                      params.set('search', f.search);
      if (f.startDate)                   params.set('startDate', new Date(f.startDate).toISOString());
      if (f.endDate)                     params.set('endDate', new Date(f.endDate + 'T23:59:59').toISOString());
      if (f.paymentMethod !== 'all')     params.set('payment_method', f.paymentMethod);
      if (f.minAmount)                   params.set('minAmount', f.minAmount);
      if (f.maxAmount)                   params.set('maxAmount', f.maxAmount);

      const res  = await fetch(`${SALON_API_URL}/bills?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        const nextRows = data.data || [];
        setBills((prev) => append ? [...prev, ...nextRows.filter((row) => !prev.some((existing) => existing.id === row.id))] : nextRows);
        setPage(pg);
        const tp = data.totalPages || Math.ceil((data.total || nextRows.length) / PAGE_SIZE) || 1;
        setTotalPages(tp);
        setTotalCount(data.total || nextRows.length);
        setTotalRevenue(data.totalRevenue || nextRows.reduce((sum, bill) => sum + parseFloat(bill.total || 0), 0));
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBills(1, filters);
  }, [fetchBills, filters]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setFilters(f => ({ ...f, search: val }));
    }, 400);
  };

  const handleFiltersChange = (newF) => { setFilters(newF); };
  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
  };
  const handleLoadMore = () => {
    if (loadingMore || page >= totalPages) return;
    fetchBills(page + 1, filters, { append: true });
  };

  const visibleCount = bills.length;

  return (
    <div className="p-6 lg:p-8 min-h-full w-full">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-900 dark:text-white">Bill History</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">All bills from the beginning</p>
        </div>

        {/* Filter button */}
        <button
          onClick={() => setDrawerOpen(true)}
          className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold shadow-sm transition-all ${
            activeFilterCount > 0
              ? 'bg-purple-600 border-purple-600 text-white'
              : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
          }`}
        >
          <SlidersHorizontal size={15}/>
          Filters
          {activeFilterCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-white text-purple-600 text-xs font-black flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* ── Summary Strip ── */}
      <div className="mb-6">
        <div className="max-w-xs bg-gradient-to-br from-purple-600 to-violet-700 rounded-2xl p-5 text-white shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-widest opacity-70">Total Bills</p>
            <Hash size={16} className="opacity-50"/>
          </div>
          <p className="text-3xl font-black">{totalCount}</p>
          <p className="text-xs opacity-60 mt-1">
            {activeFilterCount > 0 ? 'Matching filters' : 'All time'}
          </p>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="relative mb-5">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"/>
        <input
          className="w-full pl-11 pr-10 py-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-sm"
          placeholder="Search by customer name or phone number…"
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
        />
        {searchInput && (
          <button onClick={() => handleSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-lg">
            <X size={14}/>
          </button>
        )}
      </div>

      {/* ── Active filter chips ── */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {filters.search && (
            <span className="flex items-center gap-1.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-3 py-1.5 rounded-xl font-semibold">
              "{filters.search}"
              <button onClick={() => { setFilters(f=>({...f,search:''})); setSearchInput(''); }}><X size={11}/></button>
            </span>
          )}
          {filters.startDate && (
            <span className="flex items-center gap-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-xl font-semibold">
              From {filters.startDate}
              <button onClick={() => setFilters(f=>({...f,startDate:''}))}><X size={11}/></button>
            </span>
          )}
          {filters.endDate && (
            <span className="flex items-center gap-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-xl font-semibold">
              To {filters.endDate}
              <button onClick={() => setFilters(f=>({...f,endDate:''}))}><X size={11}/></button>
            </span>
          )}
          {filters.paymentMethod !== 'all' && (
            <span className="flex items-center gap-1.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1.5 rounded-xl font-semibold">
              {filters.paymentMethod.toUpperCase()}
              <button onClick={() => setFilters(f=>({...f,paymentMethod:'all'}))}><X size={11}/></button>
            </span>
          )}
          {(filters.minAmount || filters.maxAmount) && (
            <span className="flex items-center gap-1.5 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-3 py-1.5 rounded-xl font-semibold">
              {filters.minAmount && `₹${filters.minAmount}`}{filters.minAmount && filters.maxAmount && ' – '}{filters.maxAmount && `₹${filters.maxAmount}`}
              <button onClick={() => setFilters(f=>({...f,minAmount:'',maxAmount:''}))}><X size={11}/></button>
            </span>
          )}
          <button onClick={handleReset}
            className="text-xs text-gray-400 hover:text-red-500 px-2 py-1.5 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors font-semibold">
            Clear all
          </button>
        </div>
      )}

      {/* ── Bill count + page info ── */}
      {!loading && totalCount > 0 && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400">
            Showing {visibleCount} of {totalCount} bills
          </p>
          <p className="text-xs text-gray-400">Loaded {page} of {totalPages} page(s)</p>
        </div>
      )}

      {/* ── Bill List ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 gap-4">
          <Loader2 size={36} className="animate-spin text-purple-600"/>
          <p className="text-gray-400 font-medium">Loading bills…</p>
        </div>
      ) : bills.length === 0 ? (
        <div className="text-center py-24 bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700">
          <div className="w-20 h-20 rounded-3xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
            <ReceiptText size={32} className="text-gray-300 dark:text-slate-500"/>
          </div>
          <h3 className="text-xl font-black text-gray-500 dark:text-gray-400">No bills found</h3>
          <p className="text-sm text-gray-400 mt-2">
            {activeFilterCount > 0 ? 'Try adjusting or clearing your filters' : 'Bills will appear here once created'}
          </p>
          {activeFilterCount > 0 && (
            <button onClick={handleReset}
              className="mt-4 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-xl transition-colors">
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {bills.map(bill => (
            <BillRow key={bill.id || bill.bill_number} bill={bill}/>
          ))}
        </div>
      )}

      {/* ── Load More ── */}
      {!loading && page < totalPages && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-5 py-3 rounded-2xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : <ChevronDown size={14} />}
            {loadingMore ? 'Loading more bills...' : 'Load More Bills'}
          </button>
        </div>
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filters={filters}
        onChange={handleFiltersChange}
        onReset={handleReset}
      />
    </div>
  );
}